import {InfiniteData, UseInfiniteQueryOptions} from '@tanstack/react-query';
import {SyntheticEvent, useEffect} from 'react';
import {
  ReactQueryGetKeyVariablesRequired,
  ReactQueryInfiniteType,
  ReactQueryInfinityScrollFetcherChildrenType,
} from './CodegenReactQueryType.type';
import {ReactQueryInfiniteFetcherChildren} from './ReactQueryInfiniteFetcherChildren';

interface Props<QueryData> {
  type: ReactQueryInfiniteType;
  onStartFetchNextPage?: (e: SyntheticEvent<HTMLDivElement>) => void;
  onStartFetchPrevPage?: (e: SyntheticEvent<HTMLDivElement>) => void;
  loadingWrapperClassName?: string;
  children?: ReactQueryInfinityScrollFetcherChildrenType<QueryData>;
  onSuccess?: (data: InfiniteData<QueryData>) => void;
  onError?: (error: any) => void;
}

/**
 * @description
 * @east
 * react-query v5 로 마이그레이션 하면서 codegen 타입도 변경되었습니다.
 * codegen 시 queryKey를 제외한 타입으로 useInfiniteQuery가 생성됨으로
 * queryKey를 제외한 타입을 추출합니다.
 */
type ExtractOptions<O> = O extends Omit<
  UseInfiniteQueryOptions<infer Data, any, any, any, any, any>,
  'queryKey'
>
  ? Data
  : never;

/**
 * react-query 중 infiniteQuery 를 통해 데이터를 가져옵니다
 * manualFetchNextPageComponent 파라미터를 넣으면 해당 컴포넌트를 통해 메뉴얼하게 가져오고 넣지 않으면 InfinityScroll 로 가져옵니다.
 */
export function ReactQueryInfiniteFetcher<
  UseInfiniteQueryHook extends ((...args: any) => any) &
    ReactQueryGetKeyVariablesRequired<Parameters<UseInfiniteQueryHook>[0]>,
>({
  children,
  reactQuery,
  variables,
  options,
  type,
  onStartFetchNextPage,
  onStartFetchPrevPage,
  onSuccess,
  onError,
}: Props<ExtractOptions<Parameters<UseInfiniteQueryHook>[1]>> & {
  reactQuery: UseInfiniteQueryHook;
  variables: Parameters<UseInfiniteQueryHook>[0];
  options: Parameters<UseInfiniteQueryHook>[1];
}) {
  const {
    data,
    isFetching,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    error,
  } = reactQuery(variables, options);

  useEffect(() => {
    if (data && onSuccess) {
      onSuccess(data);
    }
  }, [data, onSuccess]);

  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  return (
    <ReactQueryInfiniteFetcherChildren<
      ExtractOptions<Parameters<UseInfiniteQueryHook>[1]>
    >
      isFetching={isFetching}
      isLoading={isLoading}
      data={data}
      enabled={options?.enabled}
      hasPreviousPage={hasPreviousPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      fetchPreviousPage={fetchPreviousPage}
      type={type}
      onStartFetchNextPage={onStartFetchNextPage}
      onStartFetchPrevPage={onStartFetchPrevPage}
      isFetchingNextPage={isFetchingNextPage}
    >
      {children}
    </ReactQueryInfiniteFetcherChildren>
  );
}
